import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { Modal } from '../../components/ui/modal';
import {
  api,
  apiEndpoints,
  normalizeWebsiteCrawl,
  normalizeWebsiteImportJob,
  type WebsiteCrawlResponse,
  type WebsiteDiscoveredResource,
  type WebsiteDiscoveredResourceKind,
  type WebsiteDiscoveredResourceListResponse,
  type WebsiteImportJobResponse,
  type WebsiteOperationConflictResponse,
} from '../../utils/apiService';
import { formatBytes } from '../../utils/fileUploadValidation';
import {
  boundedPollBackoff,
  isWebsiteOperationTerminal,
  KnowledgePollController,
  websiteOperationStatusLabel,
} from './knowledgeBasePolling';
import {
  categoryOptions,
  inferKnowledgeMetadata,
  scopeOptions,
  validateCrawlerUrl,
  type KnowledgeCategory,
  type KnowledgeScope,
} from './knowledgeBaseShared';

type SelectionMetadata = {
  resource: WebsiteDiscoveredResource;
  category: KnowledgeCategory;
  scope: KnowledgeScope;
  include_in_compilation: boolean;
};

interface WebsiteKnowledgeImportModalProps {
  isOpen: boolean;
  storeId: string;
  openIntent: number;
  pollController: KnowledgePollController;
  onClose: () => void;
  onImportProgress: () => void | Promise<void>;
  onImportFinished: (job: WebsiteImportJobResponse) => void | Promise<void>;
}

const POLL_MS = 2500;
const MAX_POLL_RETRIES = 4;
const MAX_POLL_BACKOFF_MS = 20_000;
const isTransientPollStatus = (status?: number) =>
  status == null ||
  status === 408 ||
  status === 425 ||
  status === 429 ||
  status >= 500;

const parseError = async (response: Response, fallback: string) => {
  const data = await response.json().catch(() => ({}));
  if (typeof data.detail === 'string') return data.detail;
  if (typeof data.error === 'string') return data.error;
  const field = Object.values(data).find(
    (value) => Array.isArray(value) && value.length,
  );
  return Array.isArray(field) ? String(field[0]) : fallback;
};

const idempotencyKey = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `website-import-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const initialMetadata = (
  resource: WebsiteDiscoveredResource,
): SelectionMetadata => {
  const inferred = inferKnowledgeMetadata(
    resource.effective_url || resource.discovered_url,
    resource.title,
  );
  return {
    resource,
    category: inferred.category,
    scope: inferred.scope,
    include_in_compilation: true,
  };
};

const WebsiteKnowledgeImportModal: React.FC<
  WebsiteKnowledgeImportModalProps
> = ({
  isOpen,
  storeId,
  openIntent,
  pollController,
  onClose,
  onImportProgress,
  onImportFinished,
}) => {
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [error, setError] = useState('');
  const [crawl, setCrawl] = useState<WebsiteCrawlResponse | null>(null);
  const [crawlSubmitting, setCrawlSubmitting] = useState(false);
  const [kind, setKind] = useState<WebsiteDiscoveredResourceKind>('html');
  const [pages, setPages] = useState({ html: 1, pdf: 1 });
  const [resourcePage, setResourcePage] =
    useState<WebsiteDiscoveredResourceListResponse | null>(null);
  const [loadingResources, setLoadingResources] = useState(false);
  const [selections, setSelections] = useState<
    Record<number, SelectionMetadata>
  >({});
  const [bulkCategory, setBulkCategory] = useState<KnowledgeCategory>('other');
  const [bulkScope, setBulkScope] = useState<KnowledgeScope>('unknown');
  const [bulkInclude, setBulkInclude] = useState(true);
  const [importJob, setImportJob] = useState<WebsiteImportJobResponse | null>(
    null,
  );
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const lastProgressCount = useRef(0);
  const generation = useRef(0);
  const isOpenRef = useRef(isOpen);
  const requests = useRef(new Set<AbortController>());
  const resourceRequest = useRef<AbortController | null>(null);
  const resourceSequence = useRef(0);
  const monitorCancels = useRef(new Set<() => void>());
  const importAttempt = useRef<{
    signature: string;
    key: string;
  } | null>(null);

  isOpenRef.current = isOpen;

  const abortRequests = useCallback(() => {
    requests.current.forEach((controller) => controller.abort());
    requests.current.clear();
    resourceRequest.current = null;
    resourceSequence.current += 1;
  }, []);

  const stopMonitoring = useCallback(() => {
    monitorCancels.current.forEach((cancel) => cancel());
    monitorCancels.current.clear();
  }, []);

  const invalidate = useCallback(() => {
    generation.current += 1;
    abortRequests();
    stopMonitoring();
  }, [abortRequests, stopMonitoring]);

  const resetState = useCallback(() => {
    setUrl('');
    setUrlError('');
    setError('');
    setCrawl(null);
    setCrawlSubmitting(false);
    setKind('html');
    setPages({ html: 1, pdf: 1 });
    setResourcePage(null);
    setLoadingResources(false);
    setSelections({});
    setBulkCategory('other');
    setBulkScope('unknown');
    setBulkInclude(true);
    setImportJob(null);
    setImportSubmitting(false);
    setCancelling(false);
    lastProgressCount.current = 0;
    importAttempt.current = null;
  }, []);

  const isCurrent = useCallback(
    (requestGeneration: number) =>
      requestGeneration === generation.current && isOpenRef.current,
    [],
  );

  const startRequest = useCallback((requestGeneration: number) => {
    const controller = new AbortController();
    if (requestGeneration !== generation.current || !isOpenRef.current) {
      controller.abort();
      return controller;
    }
    requests.current.add(controller);
    return controller;
  }, []);

  const finishRequest = useCallback((controller: AbortController) => {
    requests.current.delete(controller);
  }, []);

  useLayoutEffect(() => {
    invalidate();
    resetState();
  }, [invalidate, openIntent, resetState, storeId]);

  useLayoutEffect(() => {
    if (!isOpen) invalidate();
  }, [invalidate, isOpen]);

  useEffect(
    () => () => {
      invalidate();
    },
    [invalidate],
  );

  const selected = useMemo(() => Object.values(selections), [selections]);
  const included = selected.filter((item) => item.include_in_compilation);
  const selectedTokenCount = included.reduce(
    (total, item) => total + (item.resource.estimated_token_count || 0),
    0,
  );
  const oversized = included.filter(
    (item) => (item.resource.estimated_token_count || 0) > 50_000,
  );

  const close = useCallback(() => {
    invalidate();
    onClose();
  }, [invalidate, onClose]);

  const loadResources = useCallback(
    async (
      crawlId: number,
      resourceKind: WebsiteDiscoveredResourceKind,
      page: number,
      requestGeneration = generation.current,
    ) => {
      resourceRequest.current?.abort();
      const requestSequence = ++resourceSequence.current;
      const controller = startRequest(requestGeneration);
      if (controller.signal.aborted) return;
      resourceRequest.current = controller;
      const isLatestResourceRequest = () =>
        requestSequence === resourceSequence.current &&
        isCurrent(requestGeneration);
      setLoadingResources(true);
      setError('');
      try {
        const response = await api.get(
          apiEndpoints.getKnowledgeBaseWebsiteCrawlResources(crawlId, {
            kind: resourceKind,
            page,
          }),
          { signal: controller.signal },
        );
        if (!isLatestResourceRequest()) return;
        if (response?.ok) {
          const next =
            (await response.json()) as WebsiteDiscoveredResourceListResponse;
          if (!isLatestResourceRequest()) return;
          setResourcePage(next);
        } else if (response) {
          const message = await parseError(
            response,
            'Could not load discovered URLs.',
          );
          if (isLatestResourceRequest()) setError(message);
        }
      } catch (requestError) {
        if (
          (requestError as DOMException).name !== 'AbortError' &&
          isLatestResourceRequest()
        ) {
          setError('Could not load discovered URLs.');
        }
      } finally {
        finishRequest(controller);
        if (resourceRequest.current === controller) {
          resourceRequest.current = null;
        }
        if (isLatestResourceRequest()) setLoadingResources(false);
      }
    },
    [finishRequest, isCurrent, startRequest],
  );

  const pollCrawl = useCallback(
    (
      crawlId: number,
      requestGeneration = generation.current,
      failedAttempts = 0,
      delayMs = POLL_MS,
    ) => {
      if (!isCurrent(requestGeneration)) return;
      let cancel: () => void = () => {};
      cancel = pollController.schedule(
        storeId,
        'crawl',
        crawlId,
        delayMs,
        async (signal) => {
          monitorCancels.current.delete(cancel);
          if (!isCurrent(requestGeneration)) return;
          try {
            const response = await api.get(
              apiEndpoints.getKnowledgeBaseWebsiteCrawl(crawlId),
              { signal },
            );
            if (!isCurrent(requestGeneration)) return;
            if (!response?.ok) {
              if (
                isTransientPollStatus(response?.status) &&
                failedAttempts < MAX_POLL_RETRIES
              ) {
                setError(
                  'Discovery status is temporarily unavailable. Retrying…',
                );
                pollCrawl(
                  crawlId,
                  requestGeneration,
                  failedAttempts + 1,
                  boundedPollBackoff(
                    POLL_MS,
                    failedAttempts,
                    MAX_POLL_BACKOFF_MS,
                  ),
                );
              } else if (response) {
                const message = await parseError(
                  response,
                  'Discovery polling failed.',
                );
                if (isCurrent(requestGeneration)) setError(message);
              } else {
                setError('Discovery polling failed after several retries.');
              }
              return;
            }
            const next = normalizeWebsiteCrawl(
              (await response.json()) as WebsiteCrawlResponse,
            );
            if (!isCurrent(requestGeneration)) return;
            setError('');
            setCrawl(next);
            if (isWebsiteOperationTerminal(next.status)) {
              if (
                next.status === 'completed' ||
                next.status === 'completed_with_errors' ||
                next.status === 'exhausted'
              ) {
                await loadResources(
                  next.id,
                  kind,
                  pages[kind],
                  requestGeneration,
                );
              }
              return;
            }
            pollCrawl(crawlId, requestGeneration);
          } catch (requestError) {
            if (
              (requestError as DOMException).name !== 'AbortError' &&
              isCurrent(requestGeneration)
            ) {
              if (failedAttempts < MAX_POLL_RETRIES) {
                setError(
                  'Discovery status is temporarily unavailable. Retrying…',
                );
                pollCrawl(
                  crawlId,
                  requestGeneration,
                  failedAttempts + 1,
                  boundedPollBackoff(
                    POLL_MS,
                    failedAttempts,
                    MAX_POLL_BACKOFF_MS,
                  ),
                );
              } else {
                setError('Discovery polling failed after several retries.');
              }
            }
          }
        },
      );
      monitorCancels.current.add(cancel);
    },
    [isCurrent, kind, loadResources, pages, pollController, storeId],
  );

  const resumeCrawl = useCallback(
    async (
      operationUrl: string | undefined,
      requestGeneration: number,
      signal: AbortSignal,
    ) => {
      const crawlId = operationUrl?.match(/\/website-crawls\/(\d+)\/?/)?.[1];
      if (!crawlId) return false;
      const response = await api.get(
        apiEndpoints.getKnowledgeBaseWebsiteCrawl(crawlId),
        { signal },
      );
      if (!response?.ok || !isCurrent(requestGeneration)) return false;
      const next = normalizeWebsiteCrawl(
        (await response.json()) as WebsiteCrawlResponse,
      );
      if (!isCurrent(requestGeneration)) return false;
      setCrawl(next);
      setError('');
      if (!isWebsiteOperationTerminal(next.status)) {
        pollCrawl(next.id, requestGeneration);
      } else if (
        next.status === 'completed' ||
        next.status === 'completed_with_errors' ||
        next.status === 'exhausted'
      ) {
        await loadResources(next.id, 'html', 1, requestGeneration);
      }
      return true;
    },
    [isCurrent, loadResources, pollCrawl],
  );

  const startDiscovery = async () => {
    const validated = validateCrawlerUrl(url);
    if (!validated.ok) {
      setUrlError(validated.error);
      return;
    }
    const requestGeneration = generation.current;
    const controller = startRequest(requestGeneration);
    if (controller.signal.aborted) return;
    setUrlError('');
    setError('');
    setCrawlSubmitting(true);
    setSelections({});
    setResourcePage(null);
    setImportJob(null);
    try {
      const response = await api.post(
        apiEndpoints.createKnowledgeBaseWebsiteCrawl(),
        { url: validated.url },
        { signal: controller.signal },
      );
      if (!isCurrent(requestGeneration)) return;
      if (response?.status === 201) {
        const next = normalizeWebsiteCrawl(
          (await response.json()) as WebsiteCrawlResponse,
        );
        if (!isCurrent(requestGeneration)) return;
        setCrawl(next);
        pollCrawl(next.id, requestGeneration);
      } else if (response) {
        if (response.status === 409) {
          const conflict = ((await response.json().catch(() => ({}))) ||
            {}) as WebsiteOperationConflictResponse;
          if (
            await resumeCrawl(
              conflict.operation_url,
              requestGeneration,
              controller.signal,
            )
          ) {
            return;
          }
          if (isCurrent(requestGeneration)) {
            setError(
              conflict.detail ||
                'A website discovery is already active for this store.',
            );
          }
        } else {
          const message = await parseError(
            response,
            'Could not start website discovery.',
          );
          if (isCurrent(requestGeneration)) setError(message);
        }
      }
    } catch (requestError) {
      if (
        (requestError as DOMException).name !== 'AbortError' &&
        isCurrent(requestGeneration)
      ) {
        setError('Could not start website discovery.');
      }
    } finally {
      finishRequest(controller);
      if (isCurrent(requestGeneration)) setCrawlSubmitting(false);
    }
  };

  const changeResourcePage = (
    nextKind: WebsiteDiscoveredResourceKind,
    nextPage: number,
  ) => {
    if (!crawl) return;
    setKind(nextKind);
    setPages((current) => ({ ...current, [nextKind]: nextPage }));
    void loadResources(crawl.id, nextKind, nextPage, generation.current);
  };

  const toggleResource = (resource: WebsiteDiscoveredResource) => {
    if (resource.exclusion_class) return;
    setSelections((current) => {
      const next = { ...current };
      if (next[resource.id]) delete next[resource.id];
      else next[resource.id] = initialMetadata(resource);
      return next;
    });
  };

  const setMetadata = (
    id: number,
    patch: Partial<Omit<SelectionMetadata, 'resource'>>,
  ) => {
    setSelections((current) => ({
      ...current,
      [id]: { ...current[id], ...patch },
    }));
  };

  const applyBulkDefaults = () => {
    setSelections((current) =>
      Object.fromEntries(
        Object.entries(current).map(([id, item]) => [
          id,
          {
            ...item,
            category: bulkCategory,
            scope: bulkScope,
            include_in_compilation: bulkInclude,
          },
        ]),
      ),
    );
  };

  const pollImport = useCallback(
    (
      jobId: number,
      requestGeneration = generation.current,
      failedAttempts = 0,
      delayMs = POLL_MS,
    ) => {
      if (!isCurrent(requestGeneration)) return;
      let cancel: () => void = () => {};
      cancel = pollController.schedule(
        storeId,
        'import',
        jobId,
        delayMs,
        async (signal) => {
          monitorCancels.current.delete(cancel);
          if (!isCurrent(requestGeneration)) return;
          try {
            const response = await api.get(
              apiEndpoints.getKnowledgeBaseWebsiteImportJob(jobId),
              { signal },
            );
            if (!isCurrent(requestGeneration)) return;
            if (!response?.ok) {
              if (
                isTransientPollStatus(response?.status) &&
                failedAttempts < MAX_POLL_RETRIES
              ) {
                setError('Import status is temporarily unavailable. Retrying…');
                pollImport(
                  jobId,
                  requestGeneration,
                  failedAttempts + 1,
                  boundedPollBackoff(
                    POLL_MS,
                    failedAttempts,
                    MAX_POLL_BACKOFF_MS,
                  ),
                );
              } else if (response) {
                const message = await parseError(
                  response,
                  'Import polling failed.',
                );
                if (isCurrent(requestGeneration)) setError(message);
              } else {
                setError('Import polling failed after several retries.');
              }
              return;
            }
            const next = normalizeWebsiteImportJob(
              (await response.json()) as WebsiteImportJobResponse,
            );
            if (!isCurrent(requestGeneration)) return;
            setError('');
            setImportJob(next);
            const progress =
              next.completed_count != null || next.failed_count != null
                ? (next.completed_count || 0) + (next.failed_count || 0)
                : (next.items || []).filter(
                    (item) =>
                      !!item.result ||
                      !!item.error_message ||
                      item.phase === 'activated',
                  ).length;
            if (progress > lastProgressCount.current) {
              lastProgressCount.current = progress;
              await onImportProgress();
              if (!isCurrent(requestGeneration)) return;
            }
            if (isWebsiteOperationTerminal(next.status)) {
              await onImportFinished(next);
              return;
            }
            pollImport(jobId, requestGeneration);
          } catch (requestError) {
            if (
              (requestError as DOMException).name !== 'AbortError' &&
              isCurrent(requestGeneration)
            ) {
              if (failedAttempts < MAX_POLL_RETRIES) {
                setError('Import status is temporarily unavailable. Retrying…');
                pollImport(
                  jobId,
                  requestGeneration,
                  failedAttempts + 1,
                  boundedPollBackoff(
                    POLL_MS,
                    failedAttempts,
                    MAX_POLL_BACKOFF_MS,
                  ),
                );
              } else {
                setError('Import polling failed after several retries.');
              }
            }
          }
        },
      );
      monitorCancels.current.add(cancel);
    },
    [isCurrent, onImportFinished, onImportProgress, pollController, storeId],
  );

  const resumeImport = useCallback(
    async (
      operationUrl: string | undefined,
      requestGeneration: number,
      signal: AbortSignal,
    ) => {
      const jobId = operationUrl?.match(/\/website-import-jobs\/(\d+)\/?/)?.[1];
      if (!jobId) return false;
      const response = await api.get(
        apiEndpoints.getKnowledgeBaseWebsiteImportJob(jobId),
        { signal },
      );
      if (!response?.ok || !isCurrent(requestGeneration)) return false;
      const next = normalizeWebsiteImportJob(
        (await response.json()) as WebsiteImportJobResponse,
      );
      if (!isCurrent(requestGeneration)) return false;
      setImportJob(next);
      setError('');
      if (!isWebsiteOperationTerminal(next.status)) {
        pollImport(next.id, requestGeneration);
      }
      return true;
    },
    [isCurrent, pollImport],
  );

  const startImport = async () => {
    if (!crawl || !selected.length) return;
    const requestGeneration = generation.current;
    const items = selected
      .map((item) => ({
        resource_id: item.resource.id,
        category: item.category,
        scope: item.scope,
        include_in_compilation: item.include_in_compilation,
      }))
      .sort((left, right) => left.resource_id - right.resource_id);
    const signature = JSON.stringify({
      storeId,
      crawlId: crawl.id,
      items,
    });
    if (importAttempt.current?.signature !== signature) {
      importAttempt.current = { signature, key: idempotencyKey() };
    }
    const attempt = importAttempt.current;
    const controller = startRequest(requestGeneration);
    if (!attempt || controller.signal.aborted) return;
    setImportSubmitting(true);
    setError('');
    lastProgressCount.current = 0;
    try {
      const response = await api.post(
        apiEndpoints.importKnowledgeBaseWebsiteCrawl(crawl.id),
        {
          idempotency_key: attempt.key,
          items,
        },
        { signal: controller.signal },
      );
      if (!isCurrent(requestGeneration)) return;
      if (response) importAttempt.current = null;
      if (response?.status === 202) {
        const next = normalizeWebsiteImportJob(
          (await response.json()) as WebsiteImportJobResponse,
        );
        if (!isCurrent(requestGeneration)) return;
        setImportJob(next);
        pollImport(next.id, requestGeneration);
      } else if (response) {
        if (response.status === 409) {
          const conflict = ((await response.json().catch(() => ({}))) ||
            {}) as WebsiteOperationConflictResponse;
          if (
            await resumeImport(
              conflict.operation_url,
              requestGeneration,
              controller.signal,
            )
          ) {
            return;
          }
          if (isCurrent(requestGeneration)) {
            setError(
              conflict.detail || 'An import is already active for this store.',
            );
          }
        } else {
          const message = await parseError(
            response,
            'Could not start the import.',
          );
          if (isCurrent(requestGeneration)) setError(message);
        }
      }
    } catch (requestError) {
      if (
        (requestError as DOMException).name !== 'AbortError' &&
        isCurrent(requestGeneration)
      ) {
        setError(
          'Could not confirm whether the import started. Retry to safely resume.',
        );
      }
    } finally {
      finishRequest(controller);
      if (isCurrent(requestGeneration)) setImportSubmitting(false);
    }
  };

  const cancelOperation = async () => {
    const operation = importJob || crawl;
    if (!operation || isWebsiteOperationTerminal(operation.status)) return;
    const requestGeneration = generation.current;
    const controller = startRequest(requestGeneration);
    if (controller.signal.aborted) return;
    setCancelling(true);
    setError('');
    try {
      const endpoint = importJob
        ? apiEndpoints.cancelKnowledgeBaseWebsiteImportJob(importJob.id)
        : apiEndpoints.cancelKnowledgeBaseWebsiteCrawl(operation.id);
      const response = await api.post(
        endpoint,
        {},
        { signal: controller.signal },
      );
      if (!isCurrent(requestGeneration)) return;
      if (response?.ok) {
        const responseBody = await response.json().catch(() => operation);
        if (!isCurrent(requestGeneration)) return;
        if (importJob) {
          setImportJob(
            normalizeWebsiteImportJob(responseBody as WebsiteImportJobResponse),
          );
        } else {
          setCrawl(normalizeWebsiteCrawl(responseBody as WebsiteCrawlResponse));
        }
      } else if (response) {
        const message = await parseError(
          response,
          'Could not cancel the operation.',
        );
        if (isCurrent(requestGeneration)) setError(message);
      }
    } catch (requestError) {
      if (
        (requestError as DOMException).name !== 'AbortError' &&
        isCurrent(requestGeneration)
      ) {
        setError('Could not cancel the operation.');
      }
    } finally {
      finishRequest(controller);
      if (isCurrent(requestGeneration)) setCancelling(false);
    }
  };

  const activeOperation =
    importJob && !isWebsiteOperationTerminal(importJob.status)
      ? importJob
      : crawl && !isWebsiteOperationTerminal(crawl.status)
        ? crawl
        : null;
  const totalPages =
    resourcePage?.total_pages ||
    Math.max(
      1,
      Math.ceil((resourcePage?.count || 0) / (resourcePage?.page_size || 20)),
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      className="max-w-6xl p-6 lg:p-8"
      showCloseButton
    >
      <div className="space-y-6" aria-live="polite">
        <div className="pr-12">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Import website knowledge
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Discover same-site pages and PDFs, then classify the resources you
            want to import. Compilation remains a separate action.
          </p>
        </div>

        {!crawl ? (
          <div className="space-y-4">
            <div>
              <Label>Website URL</Label>
              <Input
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setUrlError('');
                }}
                placeholder="example.com"
                disabled={crawlSubmitting}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                HTTPS is used by default. Credentials and non-HTTP(S) URLs are
                rejected.
              </p>
              {urlError ? (
                <p className="mt-2 text-sm text-error-500">{urlError}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={startDiscovery}
              disabled={crawlSubmitting}
              className="rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {crawlSubmitting ? 'Starting discovery...' : 'Discover resources'}
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Discovery: {websiteOperationStatusLabel(crawl)}
                  </p>
                  <p className="mt-1 break-all text-xs text-gray-500">
                    {crawl.submitted_url}
                  </p>
                </div>
                {!isWebsiteOperationTerminal(crawl.status) ? (
                  <span className="inline-flex items-center gap-2 text-sm text-brand-600">
                    <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand-200 border-t-brand-500" />
                    {crawl.cancel_requested_at
                      ? 'Cancellation requested'
                      : 'Discovering'}
                  </span>
                ) : null}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span>{crawl.discovered_html_count || 0} HTML</span>
                <span>·</span>
                <span>{crawl.discovered_pdf_count || 0} PDF</span>
                <span>·</span>
                <span>{crawl.excluded_count || 0} excluded</span>
              </div>
              {crawl.status === 'completed_with_errors' ||
              crawl.status === 'exhausted' ? (
                <p className="mt-3 text-sm text-warning-700 dark:text-warning-400">
                  Discovery was partial. You can still import the resources
                  found before limits or errors were reached.
                </p>
              ) : null}
              {crawl.status === 'failed' ? (
                <p className="mt-3 text-sm text-error-500">
                  {crawl.error_message || 'Discovery failed.'}
                </p>
              ) : null}
            </div>

            {resourcePage ? (
              <>
                <div className="flex flex-wrap gap-2" role="tablist">
                  {(['html', 'pdf'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={kind === tab}
                      onClick={() => changeResourcePage(tab, pages[tab])}
                      className={`rounded-lg px-4 py-2 text-sm font-semibold ${
                        kind === tab
                          ? 'bg-brand-500 text-white'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {tab === 'html' ? 'HTML pages' : 'PDF documents'}
                    </button>
                  ))}
                </div>

                {loadingResources ? (
                  <p className="text-sm text-gray-500">Loading resources...</p>
                ) : (
                  <div className="max-h-80 space-y-2 overflow-y-auto">
                    {resourcePage.results.map((resource) => {
                      const item = selections[resource.id];
                      const verification =
                        resource.verification_state || 'unverified';
                      const excluded = !!resource.exclusion_class;
                      return (
                        <div
                          key={resource.id}
                          className="rounded-xl border border-gray-200 p-3 dark:border-gray-700"
                        >
                          <label className="flex cursor-pointer items-start gap-3">
                            <input
                              type="checkbox"
                              checked={!!item}
                              onChange={() => toggleResource(resource)}
                              disabled={excluded}
                              className="mt-1 h-4 w-4 rounded text-brand-500"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-gray-800 dark:text-white">
                                {resource.title || resource.discovered_url}
                              </span>
                              <span className="block truncate text-xs text-gray-500">
                                {resource.effective_url ||
                                  resource.discovered_url}
                              </span>
                              <span className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                                {excluded ? (
                                  <span className="font-semibold text-warning-700">
                                    Excluded: {resource.exclusion_class}
                                  </span>
                                ) : null}
                                <span>
                                  {verification === 'verified'
                                    ? `Verified ${resource.kind || resource.kind_hint}`
                                    : `Unverified ${resource.kind_hint || resource.kind} hint`}
                                </span>
                                <span>·</span>
                                <span>
                                  {resource.observed_size_bytes != null
                                    ? `${verification === 'verified' ? 'Verified' : 'Observed'} ${formatBytes(resource.observed_size_bytes)}`
                                    : 'Size unknown'}
                                </span>
                              </span>
                            </span>
                          </label>
                          {item ? (
                            <div className="mt-3 grid gap-2 pl-7 sm:grid-cols-3">
                              <select
                                aria-label={`Category for ${resource.title || resource.discovered_url}`}
                                value={item.category}
                                onChange={(event) =>
                                  setMetadata(resource.id, {
                                    category: event.target
                                      .value as KnowledgeCategory,
                                  })
                                }
                                className="rounded-lg border border-gray-300 bg-transparent px-2 py-2 text-xs dark:border-gray-700"
                              >
                                {categoryOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={`Scope for ${resource.title || resource.discovered_url}`}
                                value={item.scope}
                                onChange={(event) =>
                                  setMetadata(resource.id, {
                                    scope: event.target.value as KnowledgeScope,
                                  })
                                }
                                className="rounded-lg border border-gray-300 bg-transparent px-2 py-2 text-xs dark:border-gray-700"
                              >
                                {scopeOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={item.include_in_compilation}
                                  onChange={(event) =>
                                    setMetadata(resource.id, {
                                      include_in_compilation:
                                        event.target.checked,
                                    })
                                  }
                                />
                                Include in compilation
                              </label>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {!resourcePage.results.length ? (
                      <p className="p-4 text-center text-sm text-gray-500">
                        No {kind.toUpperCase()} resources were discovered.
                      </p>
                    ) : null}
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span>
                    Page {pages[kind]} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={pages[kind] <= 1}
                      onClick={() => changeResourcePage(kind, pages[kind] - 1)}
                      className="rounded border px-3 py-1.5 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={pages[kind] >= totalPages}
                      onClick={() => changeResourcePage(kind, pages[kind] + 1)}
                      className="rounded border px-3 py-1.5 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>

                {selected.length ? (
                  <div className="rounded-xl bg-gray-50 p-4 dark:bg-gray-800/50">
                    <p className="text-sm font-semibold">
                      Defaults for {selected.length} selected resource
                      {selected.length === 1 ? '' : 's'}
                    </p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-4">
                      <select
                        aria-label="Bulk category"
                        value={bulkCategory}
                        onChange={(event) =>
                          setBulkCategory(
                            event.target.value as KnowledgeCategory,
                          )
                        }
                        className="rounded-lg border bg-transparent px-2 py-2 text-sm"
                      >
                        {categoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Bulk scope"
                        value={bulkScope}
                        onChange={(event) =>
                          setBulkScope(event.target.value as KnowledgeScope)
                        }
                        className="rounded-lg border bg-transparent px-2 py-2 text-sm"
                      >
                        {scopeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={bulkInclude}
                          onChange={(event) =>
                            setBulkInclude(event.target.checked)
                          }
                        />
                        Include
                      </label>
                      <button
                        type="button"
                        onClick={applyBulkDefaults}
                        className="rounded-lg border px-3 py-2 text-sm font-semibold"
                      >
                        Apply defaults
                      </button>
                    </div>
                  </div>
                ) : null}

                {oversized.length || selectedTokenCount > 150_000 ? (
                  <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
                    {oversized.length
                      ? `${oversized.length} selected resource${oversized.length === 1 ? '' : 's'} may exceed the 50k per-file token limit. `
                      : ''}
                    {selectedTokenCount > 150_000
                      ? `The estimated selection (${selectedTokenCount.toLocaleString()} tokens) exceeds the 150k bundle limit.`
                      : ''}{' '}
                    Import is still allowed; verify exact post-extraction
                    warnings before compiling.
                  </div>
                ) : null}
              </>
            ) : null}

            {importJob ? (
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <p className="text-sm font-semibold">
                  Import: {websiteOperationStatusLabel(importJob)}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {importJob.completed_count || 0} completed ·{' '}
                  {importJob.failed_count || 0} failed ·{' '}
                  {importJob.total_count ||
                    importJob.items?.length ||
                    selected.length}{' '}
                  total
                </p>
                {importJob.items?.some((item) => item.error_message) ? (
                  <ul className="mt-3 space-y-1 text-xs text-error-500">
                    {importJob.items
                      .filter((item) => item.error_message)
                      .map((item) => (
                        <li key={item.id}>
                          Resource {item.resource_id}: {item.error_message}
                        </li>
                      ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {error ? (
          <div className="rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap justify-end gap-3 border-t pt-4 dark:border-gray-800">
          {activeOperation ? (
            <button
              type="button"
              onClick={cancelOperation}
              disabled={cancelling || !!activeOperation.cancel_requested_at}
              className="rounded-lg border border-error-300 px-4 py-2.5 text-sm font-semibold text-error-600 disabled:opacity-50"
            >
              {cancelling
                ? 'Cancelling...'
                : activeOperation.cancel_requested_at
                  ? 'Cancellation requested'
                  : 'Cancel operation'}
            </button>
          ) : null}
          <button
            type="button"
            onClick={close}
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium"
          >
            Close
          </button>
          {resourcePage && !importJob ? (
            <button
              type="button"
              onClick={startImport}
              disabled={!selected.length || importSubmitting}
              className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {importSubmitting
                ? 'Starting import...'
                : `Import ${selected.length} resource${selected.length === 1 ? '' : 's'}`}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default WebsiteKnowledgeImportModal;
